import type { AppDispatch } from '@/store';
import type { NavigateFunction } from 'react-router-dom';
import { toast } from 'sonner';

import {
  TUTORIAL_DEMO_NOTE,
  TUTORIAL_DEMO_PRODUCT,
} from '@/config/demoPropertySeed';
import { setGuidedMode } from '@/store/slices/guidedModeSlice';
import type { Product } from '@/types/ProductType';

type CreateProductFn = (payload: typeof TUTORIAL_DEMO_PRODUCT) => {
  unwrap: () => Promise<Product>;
};

export type StartPropertyTutorialArgs = {
  createProduct: CreateProductFn;
  existingProducts: Product[];
  dispatch: AppDispatch;
  navigate: NavigateFunction;
};

export type StartPropertyTutorialResult = {
  productId: string;
  created: boolean;
};

function resolveProductId(product: Product): string | undefined {
  return product._id ?? (product as { id?: string }).id;
}

export async function startPropertyTutorial({
  createProduct,
  existingProducts,
  dispatch,
  navigate,
}: StartPropertyTutorialArgs): Promise<StartPropertyTutorialResult | null> {
  try {
    const existing = existingProducts.find(
      (p) => p.note === TUTORIAL_DEMO_NOTE
    );
    let productId = existing ? resolveProductId(existing) : undefined;
    let created = false;

    if (!productId) {
      const createdProduct = await createProduct(
        TUTORIAL_DEMO_PRODUCT
      ).unwrap();
      productId = resolveProductId(createdProduct);
      created = true;
    }

    if (!productId) {
      toast.error('Could not start guided tour');
      return null;
    }

    dispatch(setGuidedMode(true));
    navigate(`/properties/${productId}`);

    toast.success(
      created
        ? 'Demo property ready — Guided mode is on.'
        : 'Opening your demo property — Guided mode is on.'
    );

    return { productId, created };
  } catch (err) {
    console.error('startPropertyTutorial failed', err);
    toast.error('Could not start guided tour');
    return null;
  }
}
